import { apiClient } from './client';
import type { PaymentMethod } from './types';

/** Matches Aperture.Application.Payments.PaymentSettingsDto. Secrets are never returned, only whether they are set. */
export interface PaymentSettings {
  competitionId: string;
  organizationId: string;
  entryFeeCents: number | null;
  recipientName: string | null;
  iban: string | null;
  street: string | null;
  city: string | null;
  bankTransferQrEnabled: boolean;
  stripeEnabled: boolean;
  stripeKeySet: boolean;
  stripeKeyHint: string | null;
  stripeWebhookSecretSet: boolean;
  stripeWebhookUrl: string;
  secretStorageAvailable: boolean;
}

/** Null/empty secrets keep what is saved; removeStripeKeys deletes both. */
export interface UpdatePaymentSettingsPayload {
  entryFeeCents: number | null;
  recipientName: string | null;
  iban: string | null;
  street: string | null;
  city: string | null;
  bankTransferQrEnabled: boolean;
  stripeEnabled: boolean;
  stripeSecretKey: string | null;
  stripeWebhookSecret: string | null;
  removeStripeKeys: boolean;
}

export interface BankTransfer {
  recipientName: string;
  iban: string;
  reference: string;
  purpose: string;
  upnQrPayload: string;
}

export interface MyPayment {
  entryFeeCents: number | null;
  hasPaid: boolean;
  paidVia: PaymentMethod | null;
  cardAvailable: boolean;
  bankTransfer: BankTransfer | null;
}

export const paymentsApi = {
  getSettings: (competitionId: string) =>
    apiClient.get<PaymentSettings>(`/competitions/${competitionId}/payments/settings`).then((r) => r.data),

  updateSettings: (competitionId: string, payload: UpdatePaymentSettingsPayload) =>
    apiClient.put<PaymentSettings>(`/competitions/${competitionId}/payments/settings`, payload).then((r) => r.data),

  mine: (competitionId: string) =>
    apiClient.get<MyPayment>(`/competitions/${competitionId}/payments/mine`).then((r) => r.data),

  /** On return from Stripe: the server asks Stripe whether the session was paid and marks the caller paid. */
  confirm: (competitionId: string, sessionId: string) =>
    apiClient.post<MyPayment>(`/competitions/${competitionId}/payments/checkout/confirm`, { sessionId }).then((r) => r.data),

  /** Starts a Stripe Checkout; resolves to the address to send the payer to. */
  checkout: (competitionId: string) =>
    apiClient.post<{ url: string }>(`/competitions/${competitionId}/payments/checkout`).then((r) => r.data.url),
};
